import React, { Component } from 'react';
import {
	Modal,
	Form,
	FormField,
	Input,
	DatePicker,
	Select,
	Checkbox,
	SpaceBetween,
	Button,
	Alert,
	Container,
	Box,
} from '@cloudscape-design/components';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { configStore } from '../stores/config';
import { bonusesStore } from '../stores/bonuses';
import {  bonusesEditModalStore } from "../stores/bonusesEditModal";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import cloneDeep from "lodash/cloneDeep";

const createEmptyAdPayment = () => ({
	date: "",
	amount: "",
});

const normalizeAdPayments = (payments = []) => {
	if (!Array.isArray(payments)) {
		return [];
	}

	return payments.map((payment) => ({
		date: payment?.date || "",
		amount: payment?.amount ?? "",
	}));
};

const calculateAdPaymentsTotal = (payments = []) => {
	const normalizedPayments = normalizeAdPayments(payments);
	let hasAmount = false;

	const total = normalizedPayments.reduce((sum, payment) => {
		if (payment.amount === "" || payment.amount === null || typeof payment.amount === "undefined") {
			return sum;
		}

		const amount = Number(payment.amount);
		if (Number.isNaN(amount)) {
			return sum;
		}

		hasAmount = true;
		return sum + amount;
	}, 0);

	return hasAmount ? String(total) : "";
};

class BonusesEditModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
		}
	}

	componentWillMount() {
		this.setState({
			...this.state,
			...this.props.bonusesEditModal,
		});
	}

	getActivationLifetimeDaysError = (value) => {
		if (value === '' || value === null || typeof value === 'undefined') {
			return null;
		}

		const activationLifetimeDays = Number(value);
		if (!Number.isInteger(activationLifetimeDays) || activationLifetimeDays <= 0) {
			return 'Life time promo days must be a positive whole number.';
		}

		return null;
	};


	validateField = (field, value) => {
		const { bonusesEditModal, setBonusesEditModal } = this.props;

		const errors = { ...bonusesEditModal.errors };
		const latinNoSpaces = /^[a-zA-Z0-9]+$/;
		const latinWithUnderscore = /^[a-zA-Z0-9]+(_[a-zA-Z0-9]+)*$/;

		switch (field) {
			case 'bonusCode':
				if (!latinNoSpaces.test(value) || value.length > 20) {
					errors.bonusCode = 'bonusCode Code must be 20 characters max, only Latin letters and numbers, no spaces.';
				} else {
					delete errors.bonusCode;
				}
				break;
			case 'name':
				if (!/^[a-zA-Z0-9-]+$/.test(value) || value.length > 16) {
					errors.name = 'Name must be 16 characters max, only Latin letters and numbers, no spaces.';
				} else {
					delete errors.name;
				}
				break;
			case 'reasonIdentifier':
				if (!latinWithUnderscore.test(value)) {
					errors.reasonIdentifier = 'Identifier must be Latin letters, numbers, and underscores only (e.g., Cascade_2025_1_dep).';
				} else {
					delete errors.reasonIdentifier;
				}
				break;
			case 'bonusAmount':
				if (Number(bonusesEditModal.bonusMode) === 1) {
					delete errors.bonusAmount;
					break;
				}
				const amount = parseFloat(value);
				if (isNaN(amount) || amount > 12000) {
					errors.bonusAmount = 'Bonus Amount must be a number and not exceed 12000 RUB.';
				} else {
					delete errors.bonusAmount;
				}
				break;
			case 'bonusMode':
				if (Number(value) === 1) {
					delete errors.bonusAmount;
				} else {
					delete errors.depositPercent;
					delete errors.depositApplyLimit;
					delete errors.minDeposit;
					delete errors.maxDeposit;
					delete errors.maxBonusAmount;
				}
				break;
			case 'depositPercent':
				if (Number(bonusesEditModal.bonusMode) !== 1) {
					delete errors.depositPercent;
					break;
				}
			
				break;
			case 'depositApplyLimit':
				if (Number(bonusesEditModal.bonusMode) !== 1) {
					delete errors.depositApplyLimit;
					break;
				}
				const applyLimit = Number(value);
				if (!Number.isInteger(applyLimit) || applyLimit <= 0) {
					errors.depositApplyLimit = 'Deposit Apply Limit must be a whole number greater than 0.';
				} else {
					delete errors.depositApplyLimit;
				}
				break;
			case 'minDeposit':
				if (Number(bonusesEditModal.bonusMode) !== 1) {
					delete errors.minDeposit;
					break;
				}
				const minDeposit = parseFloat(value);
				if (isNaN(minDeposit) || minDeposit < 0) {
					errors.minDeposit = 'Min Deposit must be a non-negative number.';
				} else {
					delete errors.minDeposit;
				}
				break;
			case 'maxDeposit':
				if (Number(bonusesEditModal.bonusMode) !== 1) {
					delete errors.maxDeposit;
					break;
				}
				const maxDeposit = parseFloat(value);
				if (isNaN(maxDeposit) || maxDeposit < 0) {
					errors.maxDeposit = 'Max Deposit must be a non-negative number.';
				} else if (parseFloat(bonusesEditModal.minDeposit) > maxDeposit) {
					errors.maxDeposit = 'Max Deposit must be greater than or equal to Min Deposit.';
				} else {
					delete errors.maxDeposit;
				}
				break;
			case 'maxBonusAmount':
				if (Number(bonusesEditModal.bonusMode) !== 1) {
					delete errors.maxBonusAmount;
					break;
				}
				const maxBonusAmount = parseFloat(value);
				if (isNaN(maxBonusAmount) || maxBonusAmount < 0) {
					errors.maxBonusAmount = 'Max Bonus amount must be a non-negative number.';
				} else {
					delete errors.maxBonusAmount;
				}
				break;
			case 'maxWithdrawal':
				const withdrawal = parseFloat(value);
				if (isNaN(withdrawal) || withdrawal < 100 || withdrawal > 50000) {
					errors.maxWithdrawal = 'Max Withdrawal must be between 100 and 50000 RUB.';
				} else {
					delete errors.maxWithdrawal;
				}
				break;
			case 'count':
				const activations = parseInt(value, 10);
				if (isNaN(activations) || activations > 10000) {
					errors.maxActivations = 'Max Activations must be a number and not exceed 10000.';
				} else {
					delete errors.maxActivations;
				}
				break;
			case 'depositFrom':
				const depositFrom = parseFloat(value);
				if (value && (isNaN(depositFrom) || depositFrom < 0)) {
					errors.depositFrom = 'Deposit From must be a non-negative number.';
				} else {
					delete errors.depositFrom;
				}
				break;
			case 'depositTo':
				const depositTo = parseFloat(value);
				if (value && (isNaN(depositTo) || depositTo < 0)) {
					errors.depositTo = 'Deposit To must be a non-negative number.';
				} else if (value && parseFloat(bonusesEditModal.depositFrom) > depositTo) {
					errors.depositTo = 'Deposit To must be greater than or equal to Deposit From.';
				} else {
					delete errors.depositTo;
				}
				break;
			case 'otherPromoAmount':
				const promoAmount = parseFloat(value);
				if (bonusesEditModal.otherPromoActivated && (isNaN(promoAmount) || promoAmount <= 0)) {
					errors.otherPromoAmount = 'Amount must be a positive number.';
				} else {
					delete errors.otherPromoAmount;
				}
				break;
			case 'otherPromoDays':
				const promoDays = parseInt(value, 10);
				if (bonusesEditModal.otherPromoActivated && (isNaN(promoDays) || promoDays <= 0)) {
					errors.otherPromoDays = 'Days must be a positive number.';
				} else {
					delete errors.otherPromoDays;
				}
				break;
			case 'activation_lifetime_days':
				const activationLifetimeDaysError = this.getActivationLifetimeDaysError(value);
				if (activationLifetimeDaysError) {
					errors.activation_lifetime_days = activationLifetimeDaysError;
				} else {
					delete errors.activation_lifetime_days;
				}
				break;
			case 'betAmount':
				const betAmt = parseFloat(value);
				if (bonusesEditModal.considerBetAmount && (isNaN(betAmt) || betAmt <= 0)) {
					errors.betAmount = 'Bet Amount must be a positive number.';
				} else {
					delete errors.betAmount;
				}
				break;
			case 'betDays':
				const betDays = parseInt(value, 10);
				if (bonusesEditModal.considerBetAmount && (isNaN(betDays) || betDays <= 0)) {
					errors.betDays = 'Days must be a positive number.';
				} else {
					delete errors.betDays;
				}
				break;
			case 'vipLevel':
				const vip = parseInt(value, 10);
				if (bonusesEditModal.vipLevelMode && (isNaN(vip) || vip < 0)) {
					errors.vipLevel = 'VIP Level must be a non-negative number.';
				} else {
					delete errors.vipLevel;
				}
				break;
			default:
				break;
		}

		setTimeout(() => {
			setBonusesEditModal({ ...bonusesEditModal, errors });
		}, 0);
	};


	handleChange = (field, value) => {
		const { bonusesEditModal, setBonusesEditModal } = this.props;
		const updatedState = cloneDeep(bonusesEditModal);
		updatedState[field] = value;

		setBonusesEditModal(updatedState);

		setTimeout(() => {
			this.validateField(field, value);
		}, 0);
	};

	updateAdPayments = (updater) => {
		const { bonusesEditModal, setBonusesEditModal } = this.props;
		const currentPayments = normalizeAdPayments(bonusesEditModal.adPayments);
		const nextPayments = updater(cloneDeep(currentPayments));
		const normalizedPayments = normalizeAdPayments(nextPayments);

		setBonusesEditModal({
			...bonusesEditModal,
			adPayments: normalizedPayments,
			adCostTotal: calculateAdPaymentsTotal(normalizedPayments),
		});
	};

	handleAddAdPayment = () => {
		this.updateAdPayments((currentPayments) => [
			...currentPayments,
			createEmptyAdPayment(),
		]);
	};

	handleRemoveAdPayment = (index) => {
		this.updateAdPayments((currentPayments) =>
			currentPayments.filter((_, paymentIndex) => paymentIndex !== index)
		);
	};

	handleAdPaymentChange = (index, field, value) => {
		this.updateAdPayments((currentPayments) => {
			const nextPayments = currentPayments.length ? currentPayments : [createEmptyAdPayment()];
			nextPayments[index] = {
				...(nextPayments[index] || createEmptyAdPayment()),
				[field]: value,
			};

			return nextPayments;
		});
	};

	resetBonusModeFields = (extra = {}) => {
		const { bonusesEditModal, setBonusesEditModal } = this.props;
		const errors = { ...bonusesEditModal.errors };

		delete errors.depositPercent;
		delete errors.depositApplyLimit;
		delete errors.minDeposit;
		delete errors.maxDeposit;
		delete errors.maxBonusAmount;

		setBonusesEditModal({
			...bonusesEditModal,
			show: false,
			bonusMode: 0,
			depositPercent: '',
			depositApplyLimit: '',
			minDeposit: '',
			maxDeposit: '',
			maxBonusAmount: '',
			bonusType: { label: 'All', value: 'all' },
			errors,
			...extra,
		});
	};


	handleWagerChange = ({ detail }) => {
		const selectedWager = detail.selectedOption;
		const wagerValue = parseInt(selectedWager.value, 10);
		if (wagerValue < 30) {
			this.props.setBonusesEditModal({ ...this.props.bonusesEditModal, showWagerConfirmation: true, wager: selectedWager });
		} else {
			this.props.setBonusesEditModal({ ...this.props.bonusesEditModal, showWagerConfirmation: false, wager: selectedWager });
		}
	};

	confirmWager = () => {
		this.props.setBonusesEditModal({ ...this.props.bonusesEditModal, showWagerConfirmation: false });
	};

	cancelWager = () => {
		this.props.setBonusesEditModal({ ...this.props.bonusesEditModal, wager: null, showWagerConfirmation: false });
	};




	handleSubmit = async () => {
		try {
			const { bonusesEditModal, setBonusesEditModal, Interface } = this.props;
			const body = {};
			const isPercentMode = Number(bonusesEditModal.bonusMode) === 1;
			const activationLifetimeDaysError = this.getActivationLifetimeDaysError(
				bonusesEditModal.activation_lifetime_days
			);

			if (activationLifetimeDaysError) {
				setBonusesEditModal({
					...bonusesEditModal,
					errors: {
						...bonusesEditModal.errors,
						activation_lifetime_days: activationLifetimeDaysError,
					},
				});
				return;
			}

			if (isPercentMode) {
				const errors = { ...bonusesEditModal.errors };
				let hasErrors = false;

				

				const applyLimit = Number(bonusesEditModal.depositApplyLimit);
				if (!Number.isInteger(applyLimit) || applyLimit <= 0) {
					errors.depositApplyLimit = 'Deposit Apply Limit must be a whole number greater than 0.';
					hasErrors = true;
				} else {
					delete errors.depositApplyLimit;
				}

				const minDeposit = parseFloat(bonusesEditModal.minDeposit);
				if (isNaN(minDeposit) || minDeposit < 0) {
					errors.minDeposit = 'Min Deposit must be a non-negative number.';
					hasErrors = true;
				} else {
					delete errors.minDeposit;
				}

				const maxDeposit = parseFloat(bonusesEditModal.maxDeposit);
				if (isNaN(maxDeposit) || maxDeposit < 0) {
					errors.maxDeposit = 'Max Deposit must be a non-negative number.';
					hasErrors = true;
				} else if (minDeposit > maxDeposit) {
					errors.maxDeposit = 'Max Deposit must be greater than or equal to Min Deposit.';
					hasErrors = true;
				} else {
					delete errors.maxDeposit;
				}

				const maxBonusAmount = parseFloat(bonusesEditModal.maxBonusAmount);
				if (isNaN(maxBonusAmount) || maxBonusAmount < 0) {
					errors.maxBonusAmount = 'Max Bonus amount must be a non-negative number.';
					hasErrors = true;
				} else {
					delete errors.maxBonusAmount;
				}

				setBonusesEditModal({ ...bonusesEditModal, errors });
				if (hasErrors) {
					return;
				}
			}

			for (const key in bonusesEditModal) {
				if (Object.prototype.hasOwnProperty.call(bonusesEditModal, key)) {
					const value = bonusesEditModal[key];

					if (key === 'bonusType') {
						if (isPercentMode && value && typeof value === 'object' && 'value' in value) {
							body.bonusType = value.value;
						}
					} else if (value && typeof value === 'object' && 'value' in value && 'label' in value) {
						body[key] = value.value;
					} else if (key === 'type' && value && typeof value === 'object' && 'type' in value) {
						body[key] = value.type;
					} else if (key === 'bonusMode') {
						body.bonusMode = Number(value);
					} else if (key === 'bonusAmount') {
						if (!isPercentMode) {
							body['value'] = Number(value);
						}
					} else if (key === 'depositPercent') {
						if (isPercentMode) {
							body.depositPercent = Number(value);
						}
					} else if (key === 'depositApplyLimit') {
						if (isPercentMode) {
							body.depositApplyLimit = Number(value);
						}
					} else if (key === 'minDeposit') {
						if (isPercentMode) {
							body.minDeposit = Number(value);
						}
					} else if (key === 'maxDeposit') {
						if (isPercentMode) {
							body.maxDeposit = Number(value);
						}
					} else if (key === 'activation_lifetime_days') {
						if (value === '') {
							if (value !== this.state[key]) {
								body.activationLifeTimeDays = value;
							}
						} else if (value !== null) {
							const activationLifetimeDays = Number(value);
							if (activationLifetimeDays !== Number(this.state[key])) {
								body.activationLifeTimeDays = activationLifetimeDays;
							}
						}
					} else if (key === 'maxBonusAmount') {
						if (isPercentMode) {
							body.maxBonusAmount = Number(value);
						}
					} else if (key === 'uid') {
						if (value.toString().includes(',')) {
							body['uid'] = value.toString().split(',').map((element) => Number(element));
						} else {
							body['uid'] = value;
						}
					} else if (key === 'trafficType') {
						if (value !== this.state[key]) {
							body.traffic_type = value;
						}
					} else if (key === 'trafficSource') {
						if (value !== this.state[key]) {
							body.traffic_source = value;
						}
					} else if (key === 'adPayments') {
						const adPayments = normalizeAdPayments(value)
							.filter((payment) => payment.date || payment.amount !== '')
							.map((payment) => ({
								date: payment.date || '',
								amount: payment.amount === '' ? '' : Number(payment.amount),
							}));
						const initialPayments = normalizeAdPayments(this.state[key]);
						const adPaymentsTotal = calculateAdPaymentsTotal(value);
						const initialAdPaymentsTotal =
							this.state.adCostTotal === null || typeof this.state.adCostTotal === 'undefined'
								? ''
								: String(this.state.adCostTotal);

						if (JSON.stringify(adPayments) !== JSON.stringify(initialPayments)) {
							body.ad_payments_json = adPayments;
						}
						if (adPaymentsTotal !== initialAdPaymentsTotal) {
							body.ad_cost_total = adPaymentsTotal === '' ? '' : Number(adPaymentsTotal);
						}
					} else if (key === "description" && value && typeof value === 'object' && 'Tag' in value) {
						body["note"] = value.Tag;
					} else {
						if (value !== null) {
							if (key !== "id" && key !== "promoDateFrom" && key !== "promoDateTo" && key !== "oldNote" && key !== "note" && key !== "editAll") {
								if (value !== this.state[key]) {
									body[key] = value;
								}
							} else {
								body[key] = value;
							}
						}
					}
				}
			}


			this.setState({ loading: true });
			try {
				const result = await Interface.request("set", { class: "data", module: "bonuses", method: "edit" }, body);

				Interface.addNoty("Success", "Promo was successfully edit");
			this.resetBonusModeFields({ selectedItems: [] });
				setTimeout(() => {
					Interface.loadBonuses();
				}, 0);
			} catch (error) {
				Interface.addNoty("Error", error.text || error.toString());
			} finally {
				this.setState({ loading: false });
			}
		} catch (error) {
			console.log(error);
		}
	};

	render() {
		const { setBonusesEditModal, bonusesEditModal } = this.props;


		const noteReason = {
			"compensation_for_problem_payments": "sorry",
			"reactivation_vip": "return",
			"reactivation_email": "return",
			"welcome_bonus": "wel",
			"first_deposit": "dep1",
			"second_deposit": "dep2",
			"third_deposit": "dep3",
			"fourth_deposit": "dep4",
			"fifth_deposit": "dep5",
			"holiday": "promo_holiday",
			"attraction_slots": "promo_slots",
			"Attraction to tournaments": "promo_turnir",
			"promotion": "promotion",
			"email_promo": "email_promo",
			"attraction_tournaments": "attraction_tournaments",
			"affilate": "affilate"
		};

		const reasons = [
			{ label: 'Compensation for problem payments + date', value: 'compensation_for_problem_payments' },
			{ label: 'Reactivation VIP + date', value: 'reactivation_vip' },
			{ label: 'Reactivation email + date', value: 'reactivation_email' },
			{ label: 'Welcome bonus', value: 'welcome_bonus' },
			{ label: 'First deposit', value: 'first_deposit' },
			{ label: 'Second deposit', value: 'second_deposit' },
			{ label: 'Third deposit', value: 'third_deposit' },
			{ label: 'Fourth deposit', value: 'fourth_deposit' },
			{ label: 'Fifth deposit', value: 'fifth_deposit' },
			{ label: 'Holiday', value: 'holiday' },
			{ label: 'Attraction to slots', value: 'attraction_slots' },
			{ label: 'Email Promo', value: 'email_promo' },
			{ label: 'Promotion', value: 'promotion' },
			{ label: 'Attraction to tournaments', value: 'attraction_tournaments' },
			{ label: 'Affilate', value: 'affilate' },
		];

		const wagerOptions = Array.from({ length: 8 }, (_, i) => ({
			label: `x${(i + 1) * 10}`,
			value: `${(i + 1) * 10}`,
		}));

		const vipLevelModes = [
			{ label: 'All', value: 'all' },
			{ label: 'Equal to', value: 'equal' },
			{ label: 'Above', value: 'above' },
			{ label: 'Below', value: 'below' },
		];

		const typeOptions = this.props.config.bonuses?.options?.type || [
			{ label: 'Public', value: 'public', type: 0 },
			{ label: 'Private', value: 'private', type: 1 },
		];

		const bonusModeOptions = [
			{ label: 'Fixed amount', value: 0 },
			{ label: 'Deposit percent', value: 1 },
		].filter((element) => !bonusesEditModal.advancedSettingsEnabled ? element.value === 0 : element);
		const bonusTypeOptions = [
			{ label: 'Fiat', value: 'fiat' },
			{ label: 'Crypto', value: 'crypto' },
			{ label: 'All', value: 'all' },
		];

		const selectedBonusMode =
			bonusModeOptions.find((option) => Number(option.value) === Number(bonusesEditModal.bonusMode))
			|| bonusModeOptions[0];
		const selectedBonusType =
			bonusTypeOptions.find((option) => option.value === bonusesEditModal.bonusType?.value)
			|| bonusTypeOptions[2];
		const adPayments = normalizeAdPayments(bonusesEditModal.adPayments);
		const calculatedAdCostTotal = calculateAdPaymentsTotal(adPayments);
		const displayedAdCostTotal =
			calculatedAdCostTotal !== ''
				? calculatedAdCostTotal
				: (bonusesEditModal.adCostTotal ?? '');


		return (
			<>
				<Modal
					onDismiss={this.resetBonusModeFields}
					visible={bonusesEditModal.show}
					size="max"
					header={`Edit Bonus Code (${bonusesEditModal.name})`}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button onClick={this.resetBonusModeFields}>Cancel</Button>
								<Button variant="primary" onClick={this.handleSubmit} disabled={Object.keys(bonusesEditModal.errors).length > 0 || this.state.loading}>
									Edit
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					<Form>
						<SpaceBetween direction="vertical" size="l">
							{bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Basic Settings</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										stretch
										label="Reason for Issuance"
										description="Select the reason for issuing this bonus code for analytics parsing."
										errorText={bonusesEditModal.errors.reason}
									>
										<Select
											selectedOption={bonusesEditModal.reason}
											onChange={({ detail }) => {
												this.handleChange('reason', detail.selectedOption);
												setTimeout(() => {
													this.handleChange('note', noteReason[detail.selectedOption.value]);
												}, 100);
											}}
											options={reasons}
											placeholder="Select a reason"
										/>
									</FormField>


									{bonusesEditModal.reason && (
										<FormField
											stretch
											label="Tag"
											description="Enter a unique identifier for the reason (e.g., Cascade_2025_1_dep). Use Latin letters, numbers, and underscores only."
											errorText={bonusesEditModal.errors.reasonIdentifier}
										>
											<Input
												value={bonusesEditModal.note}
												onChange={({ detail }) => this.handleChange('note', detail.value)}
												placeholder="e.g., Cascade_2025_1_dep"
											/>
										</FormField>
									)}
								</SpaceBetween>
							</Container>}

							<Container header={<Box variant="h2">Code Details</Box>}>
								<SpaceBetween direction="vertical" size="m">
									{bonusesEditModal.advancedSettingsEnabled && <FormField
										stretch
										label="BonusCode"
										description="Enter a descriptive name for the bonusCode (e.g., FirstDeposit2025). Max 20 characters, Latin letters and numbers only, no spaces."
										errorText={bonusesEditModal.errors.bonusCode}
									>
										<Input
											value={bonusesEditModal.bonusCode}
											onChange={({ detail }) => this.handleChange('bonusCode', detail.value)}
											placeholder="e.g., FirstDeposit2025"
										/>
									</FormField>}

									<FormField
										stretch
										label="Name (Player-Facing)"
										description="Enter the promo code name visible to players (e.g., NYFS). Max 16 characters, Latin letters and numbers only, no spaces."
										errorText={bonusesEditModal.errors.name}
									>
										<Input
											value={bonusesEditModal.name}
											onChange={({ detail }) => this.handleChange('name', detail.value)}
											placeholder="e.g., NYFS"
										/>
									</FormField>

									<FormField
										stretch
										label="Bonus Mode"
										description="Choose how the deposit bonus is granted."
									>
										<Select
											selectedOption={selectedBonusMode}
											onChange={({ detail }) => this.handleChange('bonusMode', detail.selectedOption.value)}
											options={bonusModeOptions}
											placeholder="Select mode"
										/>
									</FormField>

									{(Number(bonusesEditModal.bonusMode) === 1 && bonusesEditModal.advancedSettingsEnabled) ? (
										<>
											<FormField
												stretch
												label="Deposit Percent (%)"
												description="Enter the bonus percent for deposit"
												errorText={bonusesEditModal.errors.depositPercent}
											>
												<Input
													type="number"
													value={bonusesEditModal.depositPercent}
													onChange={({ detail }) => this.handleChange('depositPercent', detail.value)}
													placeholder="e.g., 50"
												/>
											</FormField>

											<FormField
												stretch
												label="Deposit Apply Limit (Deposits)"
												description="Enter the number of deposits this bonus applies to."
												errorText={bonusesEditModal.errors.depositApplyLimit}
											>
												<Input
													type="number"
													value={bonusesEditModal.depositApplyLimit}
													onChange={({ detail }) => this.handleChange('depositApplyLimit', detail.value)}
													placeholder="e.g., 3"
												/>
											</FormField>

											<FormField
												stretch
												label="Min Deposit (RUB)"
												description="Enter the minimum deposit amount required in RUB. Use 0 to indicate no deposit requirement."
												errorText={bonusesEditModal.errors.minDeposit}
											>
												<Input
													type="number"
													value={bonusesEditModal.minDeposit}
													onChange={({ detail }) => this.handleChange('minDeposit', detail.value)}
													placeholder="e.g., 300"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Max Deposit (RUB)"
												description="Enter the maximum deposit amount required in RUB. Must be greater than or equal to Min Deposit."
												errorText={bonusesEditModal.errors.maxDeposit}
											>
												<Input
													type="number"
													value={bonusesEditModal.maxDeposit}
													onChange={({ detail }) => this.handleChange('maxDeposit', detail.value)}
													placeholder="e.g., 1000"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Max Bonus amount"
												description="Enter the maximum bonus amount in RUB for subsequent deposits."
												errorText={bonusesEditModal.errors.maxBonusAmount}
											>
												<Input
													type="number"
													value={bonusesEditModal.maxBonusAmount}
													onChange={({ detail }) => this.handleChange('maxBonusAmount', detail.value)}
													placeholder="e.g., 5000"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Bonus Type"
												description="Choose which deposit type this bonus applies to."
											>
												<Select
													selectedOption={selectedBonusType}
													onChange={({ detail }) => this.handleChange('bonusType', detail.selectedOption)}
													options={bonusTypeOptions}
													placeholder="Select bonus type"
												/>
											</FormField>
										</>
									) : (
										<FormField
											stretch
											label="Bonus Amount (RUB)"
											description="Enter the bonus amount in RUB. Maximum allowed is 12000 RUB."
											errorText={bonusesEditModal.errors.bonusAmount}
										>
											<Input
												type="number"
												value={bonusesEditModal.bonusAmount}
												onChange={({ detail }) => this.handleChange('bonusAmount', detail.value)}
												placeholder="e.g., 30"
											/>
										</FormField>
									)}

									{bonusesEditModal.advancedSettingsEnabled && <FormField
										stretch
										label="Wager"
										description="Select the wager requirement (x10 to x80). A confirmation is required if less than x30."
										errorText={bonusesEditModal.errors.wager}
									>
										<Select
											selectedOption={bonusesEditModal.wager}
											onChange={this.handleWagerChange}
											options={wagerOptions}
											placeholder="Select wager"
										/>
									</FormField>}

									{bonusesEditModal.advancedSettingsEnabled && <FormField
										stretch
										label="Max Withdrawal (RUB)"
										description="Enter the maximum withdrawal amount in RUB. Must be between 100 and 50000 RUB."
										errorText={bonusesEditModal.errors.maxWithdrawal}
									>
										<Input
											type="number"
											value={bonusesEditModal.maxWithdrawal}
											onChange={({ detail }) => this.handleChange('maxWithdrawal', detail.value)}
											placeholder="e.g., 5000"
										/>
									</FormField>}

									<FormField
										stretch
										label="Max Activations"
										description="Enter the maximum number of activations for this promo code. Maximum allowed is 10000."
										errorText={bonusesEditModal.errors.maxActivations}
									>
										<Input
											type="number"
											disabled={bonusesEditModal.type.type === 2}
											value={bonusesEditModal.count}
											onChange={({ detail }) => this.handleChange('count', detail.value)}
											placeholder="e.g., 1000"
										/>
									</FormField>

									{!bonusesEditModal.advancedSettingsEnabled &&<FormField
										stretch
										label="Tag"
										description="Enter a unique identifier for the reason (e.g., Cascade_2025_1_dep). Use Latin letters, numbers, and underscores only."
										errorText={bonusesEditModal.errors.reasonIdentifier}
									>
										<Input
											value={bonusesEditModal.note}
											onChange={({ detail }) => this.handleChange('note', detail.value)}
											placeholder="e.g., Cascade_2025_1_dep"
										/>
									</FormField>}
								</SpaceBetween>
							</Container>

							{bonusesEditModal?.type?.type === 1 && bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Affiliate Traffic Metadata</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										stretch
										label="Traffic Type"
										description="Optional partner traffic type metadata for this promo code."
									>
										<Input
											value={bonusesEditModal.trafficType}
											onChange={({ detail }) => this.handleChange('trafficType', detail.value)}
											placeholder="e.g., CPA, RevShare, Stream"
										/>
									</FormField>

									<FormField
										stretch
										label="Traffic Source"
										description="Optional partner traffic source metadata for this promo code."
									>
										<Input
											value={bonusesEditModal.trafficSource}
											onChange={({ detail }) => this.handleChange('trafficSource', detail.value)}
											placeholder="e.g., Telegram channel, YouTube, Website"
										/>
									</FormField>

									<FormField
										stretch
										label="Total Ad Cost"
										description="Automatically summed from Ad Payments History."
									>
										<Input
											type="number"
											value={displayedAdCostTotal}
											readOnly={true}
											placeholder="e.g., 50000"
										/>
									</FormField>

									<FormField
										label="Ad Payments History"
										description="Optional payment history rows stored inside ad_payments_json."
									>
										<SpaceBetween direction="vertical" size="s">
											{adPayments.length === 0 ? (
												<Box variant="p">No payment history entries added yet.</Box>
											) : (
												adPayments.map((payment, index) => (
													<div
														key={`ad-payment-${index}`}
														style={{
															display: "grid",
															gridTemplateColumns: "1fr 1fr auto",
															gap: "12px",
															alignItems: "end",
														}}
													>
														<FormField stretch label={`Payment Date ${index + 1}`}>
															<DatePicker
																value={payment.date || ''}
																onChange={({ detail }) =>
																	this.handleAdPaymentChange(index, 'date', detail.value)
																}
																placeholder="YYYY-MM-DD"
															/>
														</FormField>

														<FormField stretch label={`Payment Amount ${index + 1}`}>
															<Input
																type="number"
																value={payment.amount ?? ''}
																onChange={({ detail }) =>
																	this.handleAdPaymentChange(index, 'amount', detail.value)
																}
																placeholder="e.g., 15000"
															/>
														</FormField>

														<Button
															iconName="remove"
															onClick={() => this.handleRemoveAdPayment(index)}
														>
															Remove
														</Button>
													</div>
												))
											)}

											<Button iconName="add-plus" onClick={this.handleAddAdPayment}>
												Add payment
											</Button>
										</SpaceBetween>
									</FormField>
								</SpaceBetween>
							</Container>}

							{bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Deposit Requirements</Box>}>
								<SpaceBetween direction="vertical" size="m">
									{Number(bonusesEditModal.bonusMode) !== 1 && (
										<>
											<FormField
												stretch
												label="Deposit From (RUB)"
												description="Enter the minimum deposit amount required in RUB. Use 0 to indicate no deposit requirement."
												errorText={bonusesEditModal.errors.depositFrom}
											>
												<Input
													type="number"
													value={bonusesEditModal.depositFrom}
													onChange={({ detail }) => this.handleChange('depositFrom', detail.value)}
													placeholder="e.g., 300"
													inputMode="numeric"
												/>
											</FormField>

											<FormField
												stretch
												label="Deposit To (RUB)"
												description="Enter the maximum deposit amount required in RUB. Must be greater than or equal to Deposit From."
												errorText={bonusesEditModal.errors.depositTo}
											>
												<Input
													type="number"
													value={bonusesEditModal.depositTo}
													onChange={({ detail }) => this.handleChange('depositTo', detail.value)}
													placeholder="e.g., 500"
													inputMode="numeric"
												/>
											</FormField>
										</>
									)}

									<FormField
										label="Deposits From Count"
										description="Select the required number of deposits for eligibility (e.g., 1st deposit only). Use 'No Requirement' to disable this condition."
										stretch
									>
										<Input
											type="number"
											value={bonusesEditModal.depositCount}
											onChange={({ detail }) => this.handleChange('depositCount', detail.value)}
											placeholder="e.g., 1"
											inputMode="numeric"
										/>
									</FormField>
									<FormField
											label="Deposits To Count"
											description="Select the required number of deposits for eligibility (e.g., 1st deposit only). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesEditModal.depositCountTo}
												onChange={({ detail }) => this.handleChange('depositCountTo', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>
									<SpaceBetween direction="horizontal" size="m">
										<FormField
											label="Deposit period date from/To"
											description="Specify the date range for the deposit period."
											stretch
										>
											<DateRangePicker
												onChange={({ detail }) => {
													setBonusesEditModal({
														...bonusesEditModal,
														lastDepositDateFrom: detail.value.startDate.split("+")[0],
														lastDepositDateTo: detail.value.endDate.split("+")[0],
														lastDepositDateType: detail.value.type,
													});
												}}
												value={{
													startDate: bonusesEditModal.lastDepositDateFrom,
													endDate: bonusesEditModal.lastDepositDateTo,
													type: "absolute",
												}}
												relativeOptions={[]}
												isValidRange={range => {
													if (range.type === "absolute") {
														const [startDateWithoutTime] = range.startDate.split("T");
														const [endDateWithoutTime] = range.endDate.split("T");
														if (!startDateWithoutTime || !endDateWithoutTime) {
															return {
																valid: false,
																errorMessage: "The selected date range is incomplete.",
															};
														}
														if (new Date(range.startDate) - new Date(range.endDate) > 0) {
															return {
																valid: false,
																errorMessage: "Start date must be before end date.",
															};
														}
													}
													return { valid: true };
												}}
												i18nStrings={{
													todayAriaLabel: "Today",
													nextMonthAriaLabel: "Next month",
													previousMonthAriaLabel: "Previous month",
													customRelativeRangeDurationLabel: "Duration",
													customRelativeRangeDurationPlaceholder: "Enter duration",
													customRelativeRangeOptionLabel: "Custom range",
													customRelativeRangeOptionDescription: "Define a custom range",
													customRelativeRangeUnitLabel: "Unit of time",
													formatRelativeRange: ({ startDate, endDate }) => {
														const start = new Date(startDate).toLocaleString("en-GB");
														const end = new Date(endDate).toLocaleString("en-GB");
														return `${start} - ${end}`;
													},
													formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
													dateTimeConstraintText: "Date and time must be within range.",
													renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
														`Selected from ${startDate} to ${endDate}`,
													labelRelativeRange: "Relative range",
													labelAbsoluteRange: "Absolute range",
													dateTimePickerAriaLabel: "Date and time picker",
													dateTimePickerLabel: "Date and Time",
													clearButtonAriaLabel: "Clear date range",
													relativeModeTitle: "Relative mode",
													absoluteModeTitle: "Absolute mode",
													cancelButtonLabel: "Cancel",
													applyButtonLabel: "Apply",
												}}
											/>
										</FormField>




									</SpaceBetween>

									<FormField
											label="Deposits days"
											description="Select the required number of deposits for eligibility (e.g., 1st deposit days). Use 'No Requirement' to disable this condition."
											stretch
										>
											<Input
												type="number"
												value={bonusesEditModal.depositCountDays}
												onChange={({ detail }) => this.handleChange('depositCountDays', detail.value)}
												placeholder="e.g., 1"
												inputMode="numeric"
											/>
										</FormField>

								</SpaceBetween>
							</Container>}

							{bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Tasks Requirements</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Tasks count"
										description="Select the required number of tasks for eligibility (e.g., 1st taskCount). Use 'No Requirement' to disable this condition."
										stretch
									>
										<Input
											type="number"
											value={bonusesEditModal.taskCount}
											onChange={({ detail }) => this.handleChange('taskCount', detail.value)}
											placeholder="e.g., 1"
											inputMode="numeric"
										/>
									</FormField>
									<FormField
										label="Tasks days"
										description="Select the required number of deposits for eligibility (e.g., 1st tasks days). Use 'No Requirement' to disable this condition."
										stretch
									>
										<Input
											type="number"
											value={bonusesEditModal.taskDays}
											onChange={({ detail }) => this.handleChange('taskDays', detail.value)}
											placeholder="e.g., 1"
											inputMode="numeric"
										/>
									</FormField>
								</SpaceBetween>
							</Container>}



							{bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Verification and Restrictions</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Verification Requirements"
										description="Select the verification requirements for activating this promo code."
									>
										<SpaceBetween direction="horizontal" size="m">
											<Checkbox
												checked={bonusesEditModal.emailChecked}
												onChange={({ detail }) => this.handleChange('emailChecked', detail.checked)}
											>
												Email
											</Checkbox>
											<Checkbox
												checked={bonusesEditModal.phoneChecked}
												onChange={({ detail }) => this.handleChange('phoneChecked', detail.checked)}
											>
												Phone
											</Checkbox>
											<Checkbox
												checked={bonusesEditModal.tgChecked}
												onChange={({ detail }) => this.handleChange('tgChecked', detail.checked)}
											>
												TG
											</Checkbox>
											<Checkbox
												checked={bonusesEditModal.verifiedChecked}
												onChange={({ detail }) => this.handleChange('verifiedChecked', detail.checked)}
											>
												Verified
											</Checkbox>
										</SpaceBetween>
									</FormField>

									<FormField
										label="Other Promo Activated"
										description="Check if the player has activated another promo code within a specified amount and time period."
									>
										<SpaceBetween direction="horizontal" size="m">
											<Checkbox
												checked={bonusesEditModal.otherPromoActivated}
												onChange={({ detail }) => this.handleChange('otherPromoActivated', detail.checked)}
											>
												Check if another promo was activated
											</Checkbox>
											{bonusesEditModal.otherPromoActivated && (
												<>
													<Input
														type="number"
														value={bonusesEditModal.otherPromoAmount}
														onChange={({ detail }) => this.handleChange('otherPromoAmount', detail.value)}
														placeholder="Amount in RUB"
													/>
													<DateRangePicker
														onChange={({ detail }) => {
															setBonusesEditModal({
																...bonusesEditModal,
																otherPromoDateFrom: detail.value.startDate.split("+")[0],
																otherPromoDateTo: detail.value.endDate.split("+")[0],
																otherPromoDateType: detail.value.type,
															});
														}}
														value={{
															startDate: bonusesEditModal.otherPromoDateFrom,
															endDate: bonusesEditModal.otherPromoDateTo,
															type: "absolute",
														}}
														relativeOptions={[]}
														isValidRange={range => {
															if (range.type === "absolute") {
																const [startDateWithoutTime] = range.startDate.split("T");
																const [endDateWithoutTime] = range.endDate.split("T");
																if (!startDateWithoutTime || !endDateWithoutTime) {
																	return {
																		valid: false,
																		errorMessage: "The selected date range is incomplete.",
																	};
																}
																if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																	return {
																		valid: false,
																		errorMessage: "Start date must be before end date.",
																	};
																}
															}
															return { valid: true };
														}}
														i18nStrings={{
															todayAriaLabel: "Today",
															nextMonthAriaLabel: "Next month",
															previousMonthAriaLabel: "Previous month",
															customRelativeRangeDurationLabel: "Duration",
															customRelativeRangeDurationPlaceholder: "Enter duration",
															customRelativeRangeOptionLabel: "Custom range",
															customRelativeRangeOptionDescription: "Define a custom range",
															customRelativeRangeUnitLabel: "Unit of time",
															formatRelativeRange: ({ startDate, endDate }) => {
																const start = new Date(startDate).toLocaleString("en-GB");
																const end = new Date(endDate).toLocaleString("en-GB");
																return `${start} - ${end}`;
															},
															formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
															dateTimeConstraintText: "Date and time must be within range.",
															renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
																`Selected from ${startDate} to ${endDate}`,
															labelRelativeRange: "Relative range",
															labelAbsoluteRange: "Absolute range",
															dateTimePickerAriaLabel: "Date and time picker",
															dateTimePickerLabel: "Date and Time",
															clearButtonAriaLabel: "Clear date range",
															relativeModeTitle: "Relative mode",
															absoluteModeTitle: "Absolute mode",
															cancelButtonLabel: "Cancel",
															applyButtonLabel: "Apply",
														}}
													/>
												</>
											)}
										</SpaceBetween>
									</FormField>

									<FormField
										label="Consider Bet Amount"
										description="Check if the player's bet amount meets the specified criteria over a given period."
									>
										<SpaceBetween direction="horizontal" size="m">
											<Checkbox
												checked={bonusesEditModal.considerBetAmount}
												onChange={({ detail }) => this.handleChange('considerBetAmount', detail.checked)}
											>
												Yes
											</Checkbox>
											{bonusesEditModal.considerBetAmount && (
												<>
													<Input
														type="number"
														value={bonusesEditModal.betAmount}
														onChange={({ detail }) => this.handleChange('betAmount', detail.value)}
														placeholder="Bet Amount"
													/>
													<DateRangePicker
														onChange={({ detail }) => {
															setBonusesEditModal({
																...bonusesEditModal,
																betDateFrom: detail.value.startDate.split("+")[0],
																betDateTo: detail.value.endDate.split("+")[0],
																betDateType: detail.value.type,
															});
														}}
														value={{
															startDate: bonusesEditModal.betDateFrom,
															endDate: bonusesEditModal.betDateTo,
															type: "absolute",
														}}
														relativeOptions={[]}
														isValidRange={range => {
															if (range.type === "absolute") {
																const [startDateWithoutTime] = range.startDate.split("T");
																const [endDateWithoutTime] = range.endDate.split("T");
																if (!startDateWithoutTime || !endDateWithoutTime) {
																	return {
																		valid: false,
																		errorMessage: "The selected date range is incomplete.",
																	};
																}
																if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																	return {
																		valid: false,
																		errorMessage: "Start date must be before end date.",
																	};
																}
															}
															return { valid: true };
														}}
														i18nStrings={{
															todayAriaLabel: "Today",
															nextMonthAriaLabel: "Next month",
															previousMonthAriaLabel: "Previous month",
															customRelativeRangeDurationLabel: "Duration",
															customRelativeRangeDurationPlaceholder: "Enter duration",
															customRelativeRangeOptionLabel: "Custom range",
															customRelativeRangeOptionDescription: "Define a custom range",
															customRelativeRangeUnitLabel: "Unit of time",
															formatRelativeRange: ({ startDate, endDate }) => {
																const start = new Date(startDate).toLocaleString("en-GB");
																const end = new Date(endDate).toLocaleString("en-GB");
																return `${start} - ${end}`;
															},
															formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
															dateTimeConstraintText: "Date and time must be within range.",
															renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
																`Selected from ${startDate} to ${endDate}`,
															labelRelativeRange: "Relative range",
															labelAbsoluteRange: "Absolute range",
															dateTimePickerAriaLabel: "Date and time picker",
															dateTimePickerLabel: "Date and Time",
															clearButtonAriaLabel: "Clear date range",
															relativeModeTitle: "Relative mode",
															absoluteModeTitle: "Absolute mode",
															cancelButtonLabel: "Cancel",
															applyButtonLabel: "Apply",
														}}
													/>
												</>
											)}
										</SpaceBetween>
									</FormField>

									<FormField
										stretch
										label="VIP Level Requirement"
										description="Specify the VIP level requirement for eligibility (e.g., all levels, specific level, or range)."
									>
										<SpaceBetween direction="horizontal" size="m">
											<Select
												selectedOption={bonusesEditModal.vipLevelMode}
												onChange={({ detail }) => this.handleChange('vipLevelMode', detail.selectedOption)}
												options={vipLevelModes}
												placeholder="Select mode"
											/>
											{bonusesEditModal.vipLevelMode &&  bonusesEditModal.vipLevelMode?.value !== "all" && (
												<Select
													selectedOption={
														bonusesEditModal.vipLevel !== null
															? { value: bonusesEditModal.vipLevel, label: `Level ${bonusesEditModal.vipLevel}` }
															: null
													}
													onChange={({ detail }) =>
														this.handleChange('vipLevel', detail.selectedOption ? detail.selectedOption.value : null)
													}
													options={[...Array(10)].map((_, i) => ({
														value: i+1,
														label: `Level ${i+1}`,
													}))}
													placeholder="No VIP requirement"
												/>
											)}
										</SpaceBetween>
									</FormField>
								</SpaceBetween>
							</Container>}


							{bonusesEditModal.advancedSettingsEnabled && <Container header={<Box variant="h2">Promo Settings</Box>}>
								<SpaceBetween direction="vertical" size="m">
									<FormField
										label="Promo Validity Period"
										description="Specify the date range during which the promo code is valid. It will deactivate after the 'Valid To' date."
									>
										<DateRangePicker
											onChange={({ detail }) => {
												setBonusesEditModal({
													...bonusesEditModal,
													promoDateFrom: detail.value.startDate.split("+")[0],
													promoDateTo: detail.value.endDate.split("+")[0],
													promoDateType: detail.value.type,
												});
											}}
											value={{
												startDate: bonusesEditModal.promoDateFrom,
												endDate: bonusesEditModal.promoDateTo,
												type: "absolute",
											}}
											relativeOptions={[]}
											isValidRange={range => {
												if (range.type === "absolute") {
													const [startDateWithoutTime] = range.startDate.split("T");
													const [endDateWithoutTime] = range.endDate.split("T");
													if (!startDateWithoutTime || !endDateWithoutTime) {
														return {
															valid: false,
															errorMessage: "The selected date range is incomplete.",
														};
													}
													if (new Date(range.startDate) - new Date(range.endDate) > 0) {
														return {
															valid: false,
															errorMessage: "Start date must be before end date.",
														};
													}
												}
												return { valid: true };
											}}
											i18nStrings={{
												todayAriaLabel: "Today",
												nextMonthAriaLabel: "Next month",
												previousMonthAriaLabel: "Previous month",
												customRelativeRangeDurationLabel: "Duration",
												customRelativeRangeDurationPlaceholder: "Enter duration",
												customRelativeRangeOptionLabel: "Custom range",
												customRelativeRangeOptionDescription: "Define a custom range",
												customRelativeRangeUnitLabel: "Unit of time",
												formatRelativeRange: ({ startDate, endDate }) => {
													const start = new Date(startDate).toLocaleString("en-GB");
													const end = new Date(endDate).toLocaleString("en-GB");
													return `${start} - ${end}`;
												},
												formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
												dateTimeConstraintText: "Date and time must be within range.",
												renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
													`Selected from ${startDate} to ${endDate}`,
												labelRelativeRange: "Relative range",
												labelAbsoluteRange: "Absolute range",
												dateTimePickerAriaLabel: "Date and time picker",
												dateTimePickerLabel: "Date and Time",
												clearButtonAriaLabel: "Clear date range",
												relativeModeTitle: "Relative mode",
												absoluteModeTitle: "Absolute mode",
												cancelButtonLabel: "Cancel",
												applyButtonLabel: "Apply",
											}}
										/>
									</FormField>
									<FormField
										label="Life time promo days"
										description="Sets how many days the promo code remains active after its third successful activation."
										errorText={bonusesEditModal.errors.activation_lifetime_days}
									>
										<Input
											type="number"
											value={bonusesEditModal.activation_lifetime_days}
											onChange={({ detail }) => this.handleChange('activation_lifetime_days', detail.value)}
											placeholder="e.g., 7"
											inputMode="numeric"
										/>
									</FormField>

								</SpaceBetween>
							</Container>}

						</SpaceBetween>
					</Form>
				</Modal>

				{bonusesEditModal.showWagerConfirmation && (
					<Modal
						onDismiss={this.cancelWager}
						visible={bonusesEditModal.showWagerConfirmation}
						header="Confirm Wager Selection"
						footer={
							<SpaceBetween direction="horizontal" size="xs">
								<Button onClick={this.cancelWager}>Cancel</Button>
								<Button variant="primary" onClick={this.confirmWager}>
									Confirm
								</Button>
							</SpaceBetween>
						}
					>
						<Alert type="warning">
							Are you sure you want to select a wager of x{bonusesEditModal.wager?.value}?
						</Alert>
					</Modal>
				)}
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const bonuses = useRecoilValue(bonusesStore);
		const setBonuses = useSetRecoilState(bonusesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const bonusesEditModal = useRecoilValue(bonusesEditModalStore);
		const setBonusesEditModal = useSetRecoilState(bonusesEditModalStore);
		return (
			<Component
				{...props}
				bonuses={bonuses}
				setBonuses={setBonuses}
				setConfig={setConfig}
				config={config}
				bonusesEditModal={bonusesEditModal}
				setBonusesEditModal={setBonusesEditModal}
			/>
		);
	};
}

export default withRecoil(BonusesEditModal);